import { ReactComponent as LocationIcon } from 'assets/image/svg/location.svg';
import mapboxgl from 'mapbox-gl';
import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { INITIAL_ZOOM } from 'src/utils/constants';
import { EventPinGraphic } from './EventAnchorLayer';
import { CaptionedPin } from './PinCaption';
import HotelPinGraphic from './HotelPinGraphic';
import { fitPadding, LOCATION_ICON_TIP_TO_BOTTOM, pinCaptionsCollide } from 'src/utils/mapPin.util';
import 'mapbox-gl/dist/mapbox-gl.css';

/** NPT-2067 — the event or searched place, shown next to the hotel so the distance is visible. */
export type SimpleMapAnchor = {
   latitude: number;
   longitude: number;
   label: string;
   /** NPT-2264 — a caption drawn over the pin ("Search pin", or the event's name). */
   caption?: string;
};


type SimpleMapViewProps = {
   iconSize?: number;
   id: string;
   interactive?: boolean;
   latitude: number;
   longitude: number;
   zoom?: number;
   hideAttControl?: boolean;
   markerColor?: string;
   anchor?: SimpleMapAnchor | null;
   /** NPT-2201 — draw the centre point itself as the venue / searched-place pin, labelled for screen readers. */
   centerPin?: { label: string } | null;
   /** NPT-2264 — a caption over the hotel pin (the hotel's name). */
   markerLabel?: string;
   /** NPT-2264 — draw the hotel as a bed pin matching the search flag, instead of the location icon. */
   hotelPin?: boolean;
};

/** The search flag and the hotel pin share one size, so neither looks like the odd one out. */
const PAIRED_PIN_SIZE = 40;

const SimpleMapView: React.FC<SimpleMapViewProps> = ({
   iconSize,
   id,
   interactive,
   latitude,
   longitude,
   zoom = INITIAL_ZOOM,
   hideAttControl = false,
   markerColor,
   anchor,
   centerPin,
   markerLabel,
   hotelPin,
}) => {
   // Guarding with an early return above the hooks made every hook in this
   // component conditional (react-hooks/rules-of-hooks): a coordinate arriving
   // late changes the hook count between renders, which corrupts React's hook
   // state. Compute the condition here, bail inside each effect, and keep the
   // early return below — after every hook has been called (NPT-1570).
   const hasCoordinates = !!latitude && !!longitude;
   // The anchor is only worth showing when it is a different place from the hotel.
   const hasAnchor =
      !!anchor && Number.isFinite(anchor.latitude) && Number.isFinite(anchor.longitude) && (Math.abs(anchor.latitude - latitude) > 1e-4 || Math.abs(anchor.longitude - longitude) > 1e-4);

   const mapRef: any = useRef(null);
   const mapContainerRef: any = useRef(null);

   useEffect(() => {
      if (!hasCoordinates) return;

      mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
      mapRef.current = new mapboxgl.Map({
         container: mapContainerRef.current,
         center: [longitude, latitude],
         zoom: zoom,
         interactive: !!interactive,
         attributionControl: !hideAttControl,
      });

      const el = document.createElement('div');
      el.id = id;
      const roots: ReturnType<typeof ReactDOM.createRoot>[] = [];
      const timers: ReturnType<typeof setTimeout>[] = [];

      if (centerPin) {
         el.setAttribute('role', 'img');
         el.setAttribute('aria-label', centerPin.label);
         el.title = centerPin.label;
         const root = ReactDOM.createRoot(el);
         roots.push(root);
         root.render(<EventPinGraphic size={30} />);
         new mapboxgl.Marker({ element: el, anchor: 'bottom' }).setLngLat([longitude, latitude]).addTo(mapRef.current!);
      } else if (hotelPin) {
         if (markerLabel) {
            el.setAttribute('role', 'img');
            el.setAttribute('aria-label', markerLabel);
            el.title = markerLabel;
         }
         const root = ReactDOM.createRoot(el);
         roots.push(root);
         root.render(
            <CaptionedPin caption={markerLabel}>
               <HotelPinGraphic size={PAIRED_PIN_SIZE} />
            </CaptionedPin>,
         );
         new mapboxgl.Marker({ element: el, anchor: 'bottom' }).setLngLat([longitude, latitude]).addTo(mapRef.current!);
      } else if (iconSize) {
         if (markerLabel) {
            el.setAttribute('role', 'img');
            el.setAttribute('aria-label', markerLabel);
            el.title = markerLabel;
         }
         const root = ReactDOM.createRoot(el);
         roots.push(root);
         root.render(
            <CaptionedPin caption={markerLabel}>
               <LocationIcon className={markerColor ? markerColor : 'text-orgPrimary'} width={iconSize} height={iconSize} />
            </CaptionedPin>,
         );
         // The tip, not the middle of the icon, on the point (NPT-2264): a centre anchor drew it half an icon low.
         new mapboxgl.Marker({ element: el, anchor: 'bottom', offset: [0, Math.round(iconSize * LOCATION_ICON_TIP_TO_BOTTOM)] })
            .setLngLat([longitude, latitude])
            .addTo(mapRef.current!);
      }

      if (hasAnchor && anchor) {
         const anchorEl = document.createElement('div');
         anchorEl.setAttribute('role', 'img');
         anchorEl.setAttribute('aria-label', anchor.label);
         anchorEl.title = anchor.label;
         anchorEl.style.zIndex = '2';
         const anchorRoot = ReactDOM.createRoot(anchorEl);
         roots.push(anchorRoot);
         anchorRoot.render(
            <CaptionedPin caption={anchor.caption}>
               <EventPinGraphic size={PAIRED_PIN_SIZE} />
            </CaptionedPin>,
         );
         new mapboxgl.Marker({ element: anchorEl, anchor: 'bottom' }).setLngLat([anchor.longitude, anchor.latitude]).addTo(mapRef.current!);

         // Both in view: the hotel and where the traveler is going. Pins (and their captions) stand
         // above their point, so the top keeps the most room; the sides keep room for a caption.
         const captioned = !!(markerLabel || anchor.caption);
         const fitBoth = () => {
            const map = mapRef.current;
            const container = mapContainerRef.current;
            // A modal still opening has no size yet, and mapbox silently skips a fit it cannot make
            // (the street-level view in the NPT-2264 recording): wait for the map's load instead.
            if (!map || !container?.clientWidth || !container?.clientHeight) return;
            map.fitBounds(
               [
                  [Math.min(longitude, anchor.longitude), Math.min(latitude, anchor.latitude)],
                  [Math.max(longitude, anchor.longitude), Math.max(latitude, anchor.latitude)],
               ],
               {
                  padding: fitPadding(container, captioned ? { top: 140, bottom: 110, left: 150, right: 150 } : { top: 80, bottom: 80, left: 80, right: 80 }),
                  maxZoom: 15,
                  duration: 0,
               },
            );
         };
         // A hotel next to the search centre would stack the two captions; the search pin's gives way.
         const separateCaptions = () => {
            const map = mapRef.current;
            const caption = anchorEl.querySelector<HTMLElement>('[data-pin-caption]');
            if (!map || !caption) return;
            const collide = pinCaptionsCollide(map.project([anchor.longitude, anchor.latitude]), map.project([longitude, latitude]));
            caption.style.visibility = collide ? 'hidden' : '';
         };
         const onLoad = () => {
            mapRef.current?.resize?.();
            fitBoth();
            separateCaptions();
         };
         fitBoth();
         mapRef.current.on('load', onLoad);
         mapRef.current.on('move', separateCaptions);
         // The captions render a tick after the markers (React roots render asynchronously).
         timers.push(setTimeout(separateCaptions, 0));
      }

      return () => {
         // Deferred: unmounting a root synchronously during React's own commit warns.
         timers.forEach(clearTimeout);
         setTimeout(() => roots.forEach(r => r.unmount()), 0);
         mapRef.current.remove();
      };
   }, [latitude, longitude, hasCoordinates, hasAnchor, anchor?.latitude, anchor?.longitude, anchor?.caption, centerPin?.label, markerLabel, hotelPin]);

   useEffect(() => {
      if (!hasCoordinates || hasAnchor) return;

      const center = [longitude, latitude];
      mapRef?.current?.flyTo({ center, zoom: INITIAL_ZOOM, duration: 0 });
   }, [latitude, longitude, hasCoordinates, hasAnchor]);

   if (!hasCoordinates) return null;

   return <div className="flex-1 h-full min-h-[130px] w-full" id="map-container" ref={mapContainerRef} />;
};

export default SimpleMapView;
